import BodyCopy from '@root/gatsby-contentful-core/src/components/body-copy';
import MapTooltip from '@root/joinroot.com/src/components/product-availability-map/product-availability-map-tooltip';
import Market from '@root/core/src/models/market';
import ProductAvailabilityContent from '@root/joinroot.com/src/components/product-availability-map/product-availability-content';
import ProductAvailabilityMap from '@root/joinroot.com/src/components/product-availability-map/product-availability-map';
import PropTypes from '@root/vendor/prop-types';
import React from '@root/vendor/react';
import Responsive from '@root/core/src/utils/responsive';
import propTypes from '@root/vendor/prop-types';
import useGetMarketSupport from '@root/joinroot.com/src/api/use-get-market-support';
import { Eyebrow, H1Display, H2 } from '@root/gatsby-contentful-core/src/components/text';
import { StyleSheet } from '@root/core/src/utils/styles';
import { smooth } from '@root/gatsby-contentful-core/src/utils/animation';
import { useEffect, useMemo, useRef, useState } from '@root/vendor/react';

const isBrowser = typeof window !== 'undefined';
const allMarkets = Object.keys(Market.MAPPING);

export const TITLE_TYPES = {
  H1: 'H1',
  H2: 'H2',
};

export const PRODUCT_TYPES = {
  AUTO: 'auto',
  RENTERS: 'renters',
  HOMEOWNERS: 'homeowners',
};

export default function ProductAvailabilityMapSection({
  anchor,
  body,
  eyebrow,
  title = 'Where we are',
  titleType = 'H1',
  products = [PRODUCT_TYPES.AUTO, PRODUCT_TYPES.RENTERS, PRODUCT_TYPES.HOMEOWNERS],
}) {
  const sectionRef = useRef(null);
  const tooltipRef = useRef(null);
  const [formattedMarketData, setFormattedMarketData] = useState(null);
  const [isMouseOverMap, setIsMouseOverMap] = useState(false);
  const HeadlineComponent = titleType === TITLE_TYPES.H1 ? H1Display : H2;
  const headingContainerClass = titleType.toLowerCase() === 'h1' ? 'heading1' : 'heading2';
  const allMarketSupport = useGetMarketSupport(allMarkets);

  const smoothedMouse = useMemo(() => {
    if (!isBrowser) { return; }

    return smooth({
      x: 0,
      y: 0,
    }).start(({ x, y }) => {
      if (tooltipRef.current) {
        tooltipRef.current.style.transform = `translate3d(calc(${Math.floor(x)}px - 50%), calc(${Math.floor(y)}px - 50%), 0)`;
      }
    });
  }, []);

  const updateCursorPosition = (e) => {
    const tooltipDistanceFromMouse = 80;
    const sectionTop = sectionRef.current.getBoundingClientRect().top;
    const mousePosX = e.clientX;
    const mousePosY = e.clientY - sectionTop - tooltipDistanceFromMouse;
    smoothedMouse.update({
      x: mousePosX,
      y: mousePosY,
    });
  };

  useEffect(() => () => smoothedMouse.stop(), []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section
      css={styles.availabilityMapSection}
      id={anchor}
      onMouseMove={updateCursorPosition}
      ref={sectionRef}
    >
      <div
        className={headingContainerClass}
        css={styles.headingContainer}
      >
        {eyebrow && <Eyebrow css={styles.eyebrow}>{eyebrow}</Eyebrow>}
        <HeadlineComponent css={styles.title}>{title}</HeadlineComponent>
        <BodyCopy
          content={body}
          css={styles.body}
        />
      </div>
      <ProductAvailabilityMap
        allMarketSupport={allMarketSupport}
        productAvailabilityContent={
          <ProductAvailabilityContent
            formattedMarketData={formattedMarketData}
            products={products}
          />
        }
        setFormattedMarketData={setFormattedMarketData}
        setIsMouseOverMap={setIsMouseOverMap}
      />
      <MapTooltip
        allMarketSupport={allMarketSupport}
        isMouseOverMap={isMouseOverMap}
        productAvailabilityContent={
          <ProductAvailabilityContent
            formattedMarketData={formattedMarketData}
            products={products}
          />
        }
        ref={tooltipRef}
      />
    </section>
  );
}

ProductAvailabilityMapSection.propTypes = {
  anchor: PropTypes.string,
  body: PropTypes.node,
  eyebrow: PropTypes.node,
  products: propTypes.array,
  title: PropTypes.string,
  titleType: PropTypes.oneOf(Object.values(TITLE_TYPES)),
};

const styles = StyleSheet.create({
  availabilityMapSection: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    textAlign: 'center',
    ...Responsive.md({
      padding: '100px 20px',
    }),
  },
  headingContainer: {
    '&.heading1': {
      maxWidth: 335,
    },
    '&.heading2': {
      maxWidth: 250,
    },
    marginBottom: 50,
    ...Responsive.md({
      '&.heading1': {
        maxWidth: 940,
      },
      '&.heading2': {
        maxWidth: 700,
      },
    }),
  },
  eyebrow: {
    marginBottom: 15,
  },
  title: {
    margin: '0 auto 15px',
  },
  body: {
    maxWidth: 460,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
});
